import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

const DiaryFormatter = ({ content, className = "" }) => {
  const { isDarkMode } = useTheme();

  // Parse and group diary entries by subject and class (combining sections)
  const parseAndGroupDiaryEntries = (data) => {
    try {
      let parsedData = data;
      
      // If content is a string, try to parse it as JSON
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch (parseError) {
          console.error('Error parsing diary data:', parseError);
          return {};
        }
      }
      
      // Ensure we have an array
      if (!Array.isArray(parsedData)) {
        console.warn('Diary data is not an array:', parsedData);
        return {};
      }
      
      // Group by subject -> class (combining all sections of same class)
      const grouped = {};
      
      parsedData.forEach(entry => {
        const { subject, Class, sec, teacherName } = entry;

        const classKey = Class>0?`Class ${Class}`:`${Class}`;
        
        if (!grouped[subject]) {
          grouped[subject] = {};
        }
        
        if (!grouped[subject][classKey]) {
          grouped[subject][classKey] = [];
        }
        
        // Add section and teacher info to the entry
        grouped[subject][classKey].push({
          ...entry,
          sectionDisplay: sec || 'General',
          teacherDisplay: teacherName || 'Not specified'
        });
      });
      
      return grouped;
    } catch (error) {
      console.error('Error processing diary data:', error);
      return {};
    }
  };

  const groupedEntries = parseAndGroupDiaryEntries(content);

  // Helper function to get subject color with manual theme variants
  const getSubjectColor = (subject, index) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600', 
      'from-purple-500 to-purple-600',
      'from-orange-500 to-orange-600',
      'from-red-500 to-red-600',
      'from-indigo-500 to-indigo-600',
      'from-pink-500 to-pink-600',
      'from-teal-500 to-teal-600'
    ];
    return colors[index % colors.length];
  };

  if (Object.keys(groupedEntries).length === 0) {
    return (
      <div className={`my-4 w-full ${className}`}>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-primary-dark px-5 py-3 flex items-center gap-3">
            <span className="text-lg">📔</span>
            <span className="text-white font-semibold text-sm">Diary Entries</span>
          </div>
          <div className="p-5 text-gray-500 dark:text-gray-400 text-center">
            No diary entries found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`my-4 w-full ${className}`}>
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
        <div className="bg-gradient-to-r from-primary to-primary-dark px-5 py-3 flex items-center gap-3">
          <span className="text-lg">📔</span>
          <span className="text-white font-semibold text-sm">Diary Entries</span>
        </div>
        
        <div className="p-6 space-y-8">
          {Object.entries(groupedEntries).map(([subject, classes], subjectIndex) => (
            <div key={subject} className="space-y-4">
              {/* Subject Header */}
              <div className={`bg-gradient-to-r ${getSubjectColor(subject, subjectIndex)} rounded-xl px-6 py-4 shadow-md`}>
                <h3 className="text-white font-bold text-xl flex items-center gap-3">
                  <span className="text-2xl">📚</span>
                  {subject}
                  <span className="ml-auto text-sm bg-white/20 px-3 py-1 rounded-full">
                    {Object.keys(classes).length} {Object.keys(classes).length === 1 ? 'class' : 'classes'}
                  </span>
                </h3>
              </div>
              
              {/* Classes Grid - 2 columns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {Object.entries(classes).map(([className, entries]) => {
                  // Group entries by section within this class
                  const sectionGroups = entries.reduce((acc, entry) => {
                    const section = entry.sectionDisplay;
                    if (!acc[section]) {
                      acc[section] = [];
                    }
                    acc[section].push(entry);
                    return acc;
                  }, {});

                  return (
                    <div key={className} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
                      {/* Class Header */}
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200 dark:border-gray-600">
                        <span className="text-xl">🏫</span>
                        <div className="flex-1">
                          <h4 className="text-gray-900 dark:text-white font-semibold text-lg">{className}</h4>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {Object.keys(sectionGroups).map(section => (
                              <span key={section} className="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                                Section {section}
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-white bg-blue-500 dark:bg-blue-600 px-2 py-1 rounded-full mt-2 inline-block">
                            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Entries grouped by section */}
                      <div className="space-y-4">
                        {Object.entries(sectionGroups).map(([section, sectionEntries]) => (
                          <div key={section} className="space-y-3">
                            {/* Section Header (only if more than one section) */}
                            {Object.keys(sectionGroups).length > 1 && (
                              <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                Section {section}
                              </div>
                            )}
                            
                            {/* Entries for this section */}
                            {sectionEntries.map((entry, index) => (
                              <div key={entry.id || index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
                                <span className="text-blue-500 dark:text-blue-400 mt-1 text-sm">📝</span>
                                <div className="flex-1">
                                  <div className="text-gray-900 dark:text-white text-sm leading-relaxed">
                                    {entry.content ? (
                                      <div className="text-gray-800 dark:text-gray-200 mb-2">
                                        {entry.content}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 dark:text-gray-400 italic mb-2">
                                        {subject} entry for {className} - Section {entry.sectionDisplay}
                                      </div>
                                    )}
                                    
                                    {/* Teacher and Section Info */}
                                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                                      <span className="flex items-center gap-1">
                                        <span className="text-green-600 dark:text-green-400">👨‍🏫</span>
                                        {entry.teacherDisplay}
                                      </span>
                                      {Object.keys(sectionGroups).length === 1 && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-blue-600 dark:text-blue-400">📚</span>
                                          Section {entry.sectionDisplay}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {entry.id && (
                                    <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                                      ID: {entry.id}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DiaryFormatter;
